/**
 * A focus window's body in layers, so its card can grow into it
 * (`use-card-grow.ts`): the motion layer moves and scales, the shadow plate
 * shadows only the visible height, the clip unrolls, and the card's
 * snapshot lies over the live frame until it fades. At rest only the live
 * frame shows; the layers keep their place, so the frame never remounts.
 */
import type { CSSProperties, ReactNode } from "react";
import { CardPreview } from "@/components/shapes/shared/card-preview";
import type { FrameData } from "@/lib/frame-data";
import { frameCardFamily } from "@/lib/frame-card-shape";
import type { CardGrowFlight, CardGrowLayers } from "./use-card-grow";

interface CardGrowBodyProps {
  frame: FrameData;
  flight: CardGrowFlight | null;
  layers: CardGrowLayers;
  children: ReactNode;
}

export function CardGrowBody({
  frame,
  flight,
  layers,
  children,
}: CardGrowBodyProps) {
  const growing = !!flight && !flight.reduce;
  // Media cards are the opened view's own pixels; the rest cross-fade.
  const snapshot =
    growing && frameCardFamily(frame.frameType, frame) !== "media"
      ? flight
      : null;
  return (
    <div ref={layers.motion} className="absolute inset-0 origin-top-left">
      {growing ? (
        <div
          ref={layers.plate}
          className="pointer-events-none absolute left-0 top-0 w-full shadow-card-wall-raised"
          aria-hidden="true"
        />
      ) : null}
      <div ref={layers.clip} className="absolute inset-0">
        <div ref={layers.live} className="absolute inset-0">
          {children}
        </div>
        {snapshot ? (
          <div
            ref={layers.snapshot}
            className="pointer-events-none absolute left-0 top-0 origin-top-left overflow-hidden bg-card-surface text-card-ink"
            style={
              {
                width: snapshot.card.width,
                height: snapshot.card.height,
                "--wall-fs": `${snapshot.fontSize}px`,
              } as CSSProperties
            }
            aria-hidden="true"
            data-testid={`card-grow-snapshot-${frame.frameId}`}
          >
            <CardPreview frame={frame} density="regular" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
